/**
 * Visitor-scoped teardown for the `logout` chokepoint.
 *
 * `logout` is the app's single session-teardown chokepoint (the listener in `store/index.ts`).
 * Alongside the auth tokens and the owner-scoped slices it also drops two things that belong to
 * the VISITOR at the keyboard rather than to the authenticated identity:
 *
 *   • the marketing `startProperties` hand-off (flow, campaign ids, tracking, selected package,
 *     template pre-selection), see {@link clearStartPropertiesHandoff}; and
 *   • the UI language the visitor explicitly picked this session, see
 *     {@link clearManualLanguagePick}.
 *
 * Dropping both is right when the session simply ENDS (sign-out, a token that can no longer be
 * recovered, a support agent swapping owners): the next person in this tab must not inherit the
 * previous one's attribution, flow or language.
 *
 * It is WRONG for the register / sign-in funnel. That funnel signs the current identity out
 * before minting a session for the typed email, because `sessionLoad`'s mint falls back to
 * whatever access token is in the store and the backend takes session OWNERSHIP from that token:
 * minting while the previous owner was still signed in produced a session owned by them but
 * emailed to the new address, so redeeming the link under the new owner's token failed with an
 * owner mismatch. But the visitor is the SAME person who arrived from the marketing site moments
 * earlier, and the two keys above are read immediately AFTER that sign-out:
 *
 *   • the mint reads the hand-off for `cpid`/`cid` and for the flow that fixes the session's
 *     pricing currency, which `/set-currency` cannot correct afterwards (`slices/session.ts`); and
 *   • `authenticationLoginEmail` reads the language pick to localise the magic-link email and to
 *     persist the account's language, and that pick is made on the very page being submitted.
 *
 * So the funnel passes `keepVisitorContext: true` and everything else keeps the full teardown.
 *
 * KNOWN, ACCEPTED TRADE (review R1, findings S2/S3). Two things about this are narrower than
 * "visitor-scoped" suggests, and both were weighed and accepted rather than missed:
 *
 *  1. On a shared browser, the funnel's `logout` also dispatches `resetUserPreferences()`, which
 *     nulls `loadedForUserId`. `selectPreferencesStaleForUser` requires `loaded === true`, so the
 *     identity-change listener in `store/index.ts` no longer fires when the NEXT owner redeems
 *     their magic link IN THE SAME TAB — and that listener is what used to clear these two keys
 *     at that moment. So owner A's language pick and `startProperties` stash (which can carry a
 *     `voucher_code`) can now be read under owner B's identity. It needs the link to be opened in
 *     the same tab, since sessionStorage is tab-scoped, and an email client normally opens a new
 *     one.
 *  2. `markManualLanguagePick` is not only written by the pre-auth `/login` + `/register`
 *     pickers; the authenticated Settings > Interface panel and the configurator UserMenu write
 *     it too. So the key can hold the previous OWNER's choice, not the current visitor's.
 *
 * If either bites, the fix is not to widen this helper: it is to stop clearing the auth state in
 * the funnel at all and instead mint anonymously (thread an explicit no-token flag through
 * `sessionEnsure` into `sessionLoad`'s create branch). That closes the ownership bug at its
 * actual source, leaves the identity-change listener armed, and makes this module unnecessary.
 */
import { clearManualLanguagePick } from '@/nova/config/manualLanguagePick';
import { clearStartPropertiesHandoff } from '@/utils/start-properties-handoff';

/** Payload of the `logout` action (see `slices/authentication.ts`). */
export type LogoutOptions = {
  /**
   * Keep the visitor-scoped context (marketing hand-off + explicit language pick) instead of
   * clearing it with the rest of the session. Set ONLY by the register / sign-in funnel, which
   * signs out purely to keep the previous owner's token off the session it is about to mint.
   * Omitted everywhere else, so a call site that forgets it gets the conservative full teardown.
   */
  keepVisitorContext?: boolean;
};

/** Drop the visitor-scoped context for a `logout`, unless the caller opted to keep it. */
export const clearVisitorContext = (options?: LogoutOptions): void => {
  if (options?.keepVisitorContext === true) {
    return;
  }

  clearManualLanguagePick();
  clearStartPropertiesHandoff();
};

export default clearVisitorContext;
